// The guided tours — persistence and lookups, no React. The CONTENT lives in lib/tutorials/ (the registry); this file is
// what ui/tour.tsx walks.
//
// A tour is an ordered list of coachmark steps. Each step names a SURFACE (which page it lives on) and, usually, an
// ANCHOR — the `data-tour="…"` value of the element to spotlight. The engine walks the steps, spotlighting the real
// control and letting the user click it, so the app teaches itself in place.
//
// ⚠ DISMISSAL IS PER DEVICE, like What's New (see whats-new.tsx). `localStorage["vttmaps.tour.<id>.done"]` says a tour
// finished or was skipped; `….step` remembers where an unfinished one paused so a page navigation resumes it; `….later`
// (tier 1 only) holds the time the person said "Later", and the tour offers itself once more after that. Every read
// and write is guarded — a private window throws, and a tour that cannot remember simply offers again. The keys keep
// the `vttmaps.` prefix like every other (see HANDOFF).
//
// ⚠ IDS ARE STRINGS NOW (2026-10-02), one per tutorial, and the three original ids — gm, player, table — are unchanged so
// nobody is toured twice.
import { autoStartTutorial, tutorial, TUTORIALS, type Surface, type TourStep, type Tutorial } from "./tutorials";

export type { Surface, TourStep };
export type TourId = string;
export type TourDef = Tutorial;

// ── Persistence ───────────────────────────────────────────────────────────────
const doneKey = (id: TourId) => `vttmaps.tour.${id}.done`;
const stepKey = (id: TourId) => `vttmaps.tour.${id}.step`;
const laterKey = (id: TourId) => `vttmaps.tour.${id}.later`;

export function isTourDone(id: TourId): boolean {
  try { return localStorage.getItem(doneKey(id)) === "1"; } catch { return false; }
}
export function markTourDone(id: TourId): void {
  try { localStorage.setItem(doneKey(id), "1"); localStorage.removeItem(stepKey(id)); localStorage.removeItem(laterKey(id)); } catch { /* offers again */ }
}
export function savedStep(id: TourId): string | null {
  try { return localStorage.getItem(stepKey(id)); } catch { return null; }
}
export function saveStep(id: TourId, key: string): void {
  try { localStorage.setItem(stepKey(id), key); } catch { /* resume best-effort */ }
}
/** Start over from the top — what the Tutorials page's Start does. Clears the done flag so it runs again. */
export function resetTour(id: TourId): void {
  try { localStorage.removeItem(doneKey(id)); localStorage.removeItem(stepKey(id)); } catch { /* best-effort */ }
}

/** "Later" on a first-run tour: not now, offer once more. */
export const LATER_MS = 12 * 60 * 60 * 1000;
export function markLater(id: TourId, now = Date.now()): void {
  try {
    const n = Number(localStorage.getItem(laterKey(id))?.split(":")[1] ?? 0) + 1;
    localStorage.setItem(laterKey(id), `${now}:${n}`);
    localStorage.removeItem(stepKey(id));
  } catch { /* offers again */ }
}
/**
 * May the first-run tour offer itself now? Never while a Later is fresh; and after the SECOND Later it is done — two "not
 * now"s is a no, and a third popup is how people learn to hate a tutorial.
 */
export function mayAutoStart(id: TourId, now = Date.now()): boolean {
  try {
    const v = localStorage.getItem(laterKey(id));
    if (!v) return true;
    const [at, n] = v.split(":").map(Number);
    if ((n ?? 0) >= 2) return false;
    return now - (at ?? 0) >= LATER_MS;
  } catch { return true; }
}

// ── Who is new ────────────────────────────────────────────────────────────────
// ⚠ AUTO-START IS FOR A FIRST-TIMER ONLY, never an existing user on their next visit. The GM's signal is "runs zero
// campaigns" (self-correcting — gone the moment they make one). The player's is this marker, set once when they first
// JOIN a table (join-page.tsx); it carries them through the home tour AND the at-the-table tour, then is cleared.
const NEW_PLAYER_KEY = "vttmaps.tour.player.new";
export function markNewPlayer(): void { try { localStorage.setItem(NEW_PLAYER_KEY, "1"); } catch { /* no tour, no harm */ } }
export function isNewPlayer(): boolean { try { return localStorage.getItem(NEW_PLAYER_KEY) === "1"; } catch { return false; } }
export function clearNewPlayer(): void { try { localStorage.removeItem(NEW_PLAYER_KEY); } catch { /* best-effort */ } }

// ── The tours ───────────────────────────────────────────────────────────────
/** Every tutorial by id. */
export const TOURS: Readonly<Record<TourId, TourDef>> = Object.fromEntries(TUTORIALS.map((t) => [t.id, t]));
export const tourOf = (id: TourId): TourDef | undefined => tutorial(id);

/** The tier-1 tutorial to auto-start on this surface for a first-time visitor, on this kind of screen. */
export function autoStartFor(surface: Surface, phone = false): TourId | null {
  return autoStartTutorial(surface, phone)?.id ?? null;
}

/** Split a body into paragraphs on blank lines. */
export function paragraphs(body: string): string[] {
  return body.split(/\n\s*\n/).map((p) => p.replace(/\n/g, " ").trim()).filter(Boolean);
}
